/**
 * Centralized Popup Manager
 * - Tracks which popups have been shown (localStorage — persists forever)
 * - Enforces a cooldown between popups (no back-to-back bombardment)
 * - Ensures only 1 popup visible at a time across the entire app
 *
 * Storage safety: only popup dismissal timestamps (`{ popupId: ts }`) and the
 * "last shown" timestamp are persisted. No PII, no auth tokens, no payment
 * data — XSS-safe.
 */

const HISTORY_KEY = 'ninja_popup_history';
const COOLDOWN_KEY = 'ninja_popup_last_shown';
const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes between any two popups

let activePopupId = null;

/** Check if a popup is allowed to show right now */
export function canShowPopup(popupId) {
  // Already shown this popup? Never show again.
  const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}');
  if (history[popupId]) return false;

  // Another popup is currently active?
  if (activePopupId && activePopupId !== popupId) return false;

  // Cooldown from a recent popup?
  const lastShown = parseInt(localStorage.getItem(COOLDOWN_KEY) || '0', 10);
  if (Date.now() - lastShown < COOLDOWN_MS) return false;

  return true;
}

/** Register that a popup is now visible */
export function registerPopupShown(popupId) {
  activePopupId = popupId;
}

/** Mark popup as dismissed — records in permanent history + starts cooldown */
export function markPopupDismissed(popupId) {
  const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}');
  history[popupId] = Date.now();
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
  if (activePopupId === popupId) activePopupId = null;
}

/** Check if this specific popup was already permanently dismissed */
export function wasPopupShown(popupId) {
  const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}');
  return !!history[popupId];
}

/** Release a popup without recording permanent history (for popups with their
 *  own time-windowed suppression, e.g. the Bimini awareness popup). Starts the
 *  shared cooldown so other popups don't fire back-to-back. */
export function releasePopup(popupId) {
  try { localStorage.setItem(COOLDOWN_KEY, String(Date.now())); } catch { /* storage may be blocked */ }
  if (activePopupId === popupId) activePopupId = null;
}
